import { useCallback } from 'react';
import { create } from 'zustand';
import { en, type MessageKey } from './locales/en';
import type { Message } from './types';

export type { Message } from './types';
export type { MessageKey } from './locales/en';

/**
 * The app's language. Small and dependency-free on purpose: every visible phrase is a key
 * (`till.shiftOpen`), English (locales/en) is the source of the keys, and each other language
 * is a folder of tables with the same keys (locales/ta). Components only ever name keys.
 *
 * Tamil ships as its own chunk: it is fetched the moment someone picks it (or at boot, when
 * this browser already remembers it), and an English-only store never downloads it. Until it
 * arrives the table below is empty and every key falls back to English, so nothing can break
 * mid-load; the language itself only flips once its tables are in.
 *
 * Adding a language: copy locales/ta to locales/<code>, translate it (the type check lists every
 * key still missing), and add one entry to LANGUAGES below. Nothing else changes.
 *
 * The choice is per browser (a counter may run in Tamil while the back office stays in English)
 * and remembered there; until someone picks, the store's default (`ui.default_language`, served
 * in /pos/config) applies. Storage can be missing or blocked, so every access is guarded.
 */

/** Filled in place by `loadTamil`; the LANGUAGES entry below keeps pointing at this object. */
const taTable: Partial<Record<MessageKey, Message>> = {};
let taLoaded = false;
let taLoading: Promise<void> | null = null;

/** Load the Tamil tables once. Safe to call from anywhere; the chunk is fetched on demand. */
export function loadTamil(): Promise<void> {
  taLoading ??= import('./locales/ta')
    .then((module) => {
      Object.assign(taTable, module.ta);
      taLoaded = true;
    })
    .catch((error: unknown) => {
      // A failed chunk fetch must not brick the switch: stay on English and allow a retry.
      taLoading = null;
      throw error;
    });
  return taLoading;
}

/** Boot helper: when this browser remembers Tamil, fetch its tables before the first render. */
export function preloadStoredLanguage(): Promise<void> {
  return readStored() === 'ta' ? loadTamil().catch(() => undefined) : Promise.resolve();
}

export const LANGUAGES = [
  { code: 'en', label: 'English', short: 'EN', locale: 'en-IN', table: en },
  // A system Tamil font draws the one-letter button, so the switch never pulls a web font.
  { code: 'ta', label: 'தமிழ்', short: 'த', locale: 'ta-IN', table: taTable, font: "'Nirmala UI', 'Noto Sans Tamil UI', 'Latha', system-ui, sans-serif" },
] as const satisfies readonly {
  code: string;
  label: string;
  short: string;
  locale: string;
  table: Partial<Record<MessageKey, Message>>;
  font?: string;
}[];

export type Language = (typeof LANGUAGES)[number]['code'];

const STORAGE_KEY = 'sp.language';
const byCode = new Map<string, (typeof LANGUAGES)[number]>(LANGUAGES.map((entry) => [entry.code, entry]));

export function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && byCode.has(value);
}

function readStored(): Language | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return isLanguage(value) ? value : null;
  } catch {
    return null;
  }
}

interface LanguageState {
  /** What this browser shows. */
  language: Language;
  /** True once someone chose in this browser; the store default no longer applies. */
  chosen: boolean;
  setLanguage: (language: Language) => void;
  /** The store's default, applied only while nobody has chosen in this browser. */
  applyStoreDefault: (language: string | null | undefined) => void;
  /** Follow the till in the customer window without persisting its store default as a choice. */
  followTillLanguage: (language: Language) => void;
}

function applyToDocument(language: Language): void {
  try {
    document.documentElement.lang = language;
  } catch {
    // no document (tests)
  }
}

export const useLanguage = create<LanguageState>((set, get) => {
  const stored = readStored();
  if (stored) applyToDocument(stored);
  /**
   * Apply a language once its tables are available. Tamil arrives in a separate chunk, so the
   * switch completes a moment later; the current language stays until then — no half state.
   */
  const apply = (language: Language, chosen?: true) => {
    const commit = () => {
      set(chosen ? { language, chosen } : { language });
      applyToDocument(language);
    };
    if (language === 'ta' && !taLoaded) {
      void loadTamil().then(commit).catch(() => undefined);
      return;
    }
    commit();
  };
  return {
    language: stored ?? 'en',
    chosen: stored !== null,
    setLanguage(language) {
      try {
        localStorage.setItem(STORAGE_KEY, language);
      } catch {
        // Not remembered in this browser; it still applies until the page reloads.
      }
      apply(language, true);
    },
    applyStoreDefault(language) {
      if (get().chosen || !isLanguage(language)) return;
      apply(language);
    },
    followTillLanguage(language) {
      apply(language);
    },
  };
});

// The customer display is another window of the same browser: follow the till's choice live.
try {
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY && isLanguage(event.newValue)) {
      const next = event.newValue;
      const commit = () => {
        useLanguage.setState({ language: next, chosen: true });
        applyToDocument(next);
      };
      if (next === 'ta' && !taLoaded) void loadTamil().then(commit).catch(() => undefined);
      else commit();
    }
  });
} catch {
  // no window (tests)
}

const missing = new Set<string>();

/** One phrase in a language, English when the language lacks it. */
export function translate(language: Language, key: MessageKey, vars: Record<string, string | number> = {}): string {
  const entry = byCode.get(language);
  let message: Message | undefined = (entry?.table as Partial<Record<MessageKey, Message>> | undefined)?.[key];
  if (message === undefined) {
    // While Tamil is still loading its table is empty by design; only warn once it has arrived.
    if (language !== 'en' && taLoaded && import.meta.env?.DEV && !missing.has(key)) {
      missing.add(key);
      console.warn(`[i18n] no ${language} text for "${key}"; showing English`);
    }
    message = en[key];
  }
  // A key no table has (a dynamic `as MessageKey` built from server data newer than this build)
  // shows the key itself rather than taking the whole screen down.
  if (message === undefined) return String(key);
  let text: string;
  if (typeof message === 'string') {
    text = message;
  } else {
    const count = Number(vars.count ?? 0);
    const form = new Intl.PluralRules(entry?.locale ?? 'en-IN').select(count);
    text = form === 'one' ? message.one : message.other;
  }
  return text.replace(/\{(\w+)\}/g, (match, name: string) => (vars[name] === undefined ? match : String(vars[name])));
}

export type Translate = (key: MessageKey, vars?: Record<string, string | number>) => string;

/** The translator for the current language; re-renders the component when the language changes. */
export function useT(): Translate {
  const language = useLanguage((state) => state.language);
  return useCallback((key, vars) => translate(language, key, vars), [language]);
}

/**
 * The current language, read once, for code outside React (API errors, store messages, toasts
 * built in event handlers). It does not re-render anything: use `useT` for what stays on screen.
 */
export const tr: Translate = (key, vars) => translate(useLanguage.getState().language, key, vars);

/** The Intl locale of the current language, for dates and weekday names. */
export function currentLocale(): string {
  return byCode.get(useLanguage.getState().language)?.locale ?? 'en-IN';
}

/** A key chosen at runtime (e.g. from an enum value), when it may not exist: the fallback otherwise. */
export function hasKey(key: string): key is MessageKey {
  return key in en;
}

/** A cashier-facing message for an API error code, or the server's own English text. */
export function errorMessage(language: Language, code: string | undefined, fallback: string): string {
  if (!code) return fallback;
  const key = `error.${code}`;
  if (!hasKey(key)) return fallback;
  return language === 'en' ? fallback : translate(language, key);
}

/**
 * The original dictionary hook (customer drawer and profile): the same keys, now served by
 * the app's language store, so one language choice drives every screen.
 */
export function useTranslation(): {
  language: Language;
  t: (key: MessageKey) => string;
  setLanguage: (language: Language) => void;
} {
  const language = useLanguage((state) => state.language);
  const setLanguage = useLanguage((state) => state.setLanguage);
  return {
    language,
    t: (key) => translate(language, key),
    setLanguage,
  };
}
